import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  ActivityIndicator,
  TouchableOpacity,
  Modal,
  Platform,
  AppState,
  type AppStateStatus,
} from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { getRestauranteByCodigoQr, type Restaurante } from '../database/database';
import { getImageDisplayUrl } from '../lib/utils/imageTransform';
import {
  fetchParticipacionEdicionActivaRestaurante,
  type ParticipacionEdicionActiva,
} from '../lib/services/restauranteParticipacionEdicion';
import { RestauranteEdicionActivaParticipacionCard } from './RestauranteEdicionActivaParticipacionCard';
import { PanelEstiloVotoRegistrado } from './PanelEstiloVotoRegistrado';
import { propuestasService, type Propuesta } from '../lib/services/propuestas';
import { PropuestaPortadaVotacion } from './PropuestaPortadaVotacion';
import {
  closestUbicacionKmForPropuesta,
  distanciaVotoExcedeMaximoPermitido,
  formatMensajeErrorUbicacionVotoUaUrMdd,
  referenciaCoordsPropuesta,
  type ClosestUbicacionPropuesta,
} from '../lib/utils/propuestaDistancia';
import { restauranteBypassRangoVotacionActivo } from '../lib/utils/restauranteBypassVotacion';
import { getCurrentUserPosition, type UserGeoCoords } from '../lib/utils/userGeolocation';
import {
  consumirUbicacionBypassVotarQr,
  marcarUbicacionBypassVotarQr,
} from '../lib/votacionUbicacionBypassStorage';

/** Logo As Gastronómico (tenedor, negativo para fondos oscuros), desde `public/assets`. */
const LOGO_AS_GASTRONOMICO_URI = '/assets/Logo-As-Color-Negativo-Tenedor.svg';

export function RestaurantePublicQrPage() {
  const navigate = useNavigate();
  const { codigoQr } = useParams<{ codigoQr: string }>();
  const decoded = codigoQr ? decodeURIComponent(codigoQr) : '';
  const [data, setData] = useState<Restaurante | null | undefined>(undefined);
  /** `undefined` = aún no resuelto; `null` = error o no aplica; objeto = listo. */
  const [participacion, setParticipacion] = useState<ParticipacionEdicionActiva | null | undefined>(
    undefined,
  );
  /** `undefined` = pendiente cuando hace falta; `null` = no aplica o no encontrada. */
  const [propuestaVotacion, setPropuestaVotacion] = useState<Propuesta | null | undefined>(undefined);

  const [ubicacionQrBloqueo, setUbicacionQrBloqueo] = useState(false);
  const [ubicacionQrFuera, setUbicacionQrFuera] = useState(false);
  const [ubicacionQrBypass, setUbicacionQrBypass] = useState(false);
  const [ubicacionQrComprobando, setUbicacionQrComprobando] = useState(false);
  const [coordsQr, setCoordsQr] = useState<UserGeoCoords | null>(null);
  const coordsQrRef = useRef<UserGeoCoords | null>(null);
  const ubicacionQrVerificadaParaPropuestaRef = useRef<string | null>(null);
  const [distanciaQrClosest, setDistanciaQrClosest] = useState<ClosestUbicacionPropuesta | null>(null);

  useEffect(() => {
    ubicacionQrVerificadaParaPropuestaRef.current = null;
  }, [decoded]);

  useEffect(() => {
    if (!decoded) {
      setData(null);
      return;
    }
    let cancel = false;
    getRestauranteByCodigoQr(decoded).then((r) => {
      if (!cancel) {
        setData(r);
        if (r?.id) {
          setParticipacion(undefined);
          setPropuestaVotacion(undefined);
        }
      }
    });
    return () => {
      cancel = true;
    };
  }, [decoded]);

  useEffect(() => {
    if (!data?.id) {
      setParticipacion(null);
      setPropuestaVotacion(null);
      return;
    }
    let cancel = false;
    setParticipacion(undefined);
    setPropuestaVotacion(undefined);
    fetchParticipacionEdicionActivaRestaurante(data.id)
      .then((p) => {
        if (!cancel) {
          setParticipacion(p);
          if (p.participa && p.propuestaIdVotacion) {
            setPropuestaVotacion(undefined);
          } else {
            setPropuestaVotacion(null);
          }
        }
      })
      .catch(() => {
        if (!cancel) {
          setParticipacion(null);
          setPropuestaVotacion(null);
        }
      });
    return () => {
      cancel = true;
    };
  }, [data?.id]);

  useEffect(() => {
    if (!participacion?.participa || !participacion.propuestaIdVotacion) {
      return;
    }
    let cancel = false;
    setPropuestaVotacion(undefined);
    propuestasService
      .getById(participacion.propuestaIdVotacion)
      .then((p) => {
        if (!cancel) {
          setPropuestaVotacion(p ?? null);
        }
      })
      .catch(() => {
        if (!cancel) {
          setPropuestaVotacion(null);
        }
      });
    return () => {
      cancel = true;
    };
  }, [participacion?.participa, participacion?.propuestaIdVotacion]);

  const necesitaUbicacionParaVotarQr = useMemo(
    () =>
      Boolean(
        participacion?.participa &&
          participacion.propuestaIdVotacion &&
          propuestaVotacion != null,
      ),
    [participacion?.participa, participacion?.propuestaIdVotacion, propuestaVotacion],
  );

  const aplicarCoordsBypassUbicacionQr = useCallback((p: Propuesta) => {
    const ref = referenciaCoordsPropuesta(p);
    if (ref) {
      setCoordsQr({
        latitude: ref.lat,
        longitude: ref.lng,
        accuracy: null,
      });
      setDistanciaQrClosest(closestUbicacionKmForPropuesta(p, ref.lat, ref.lng));
    }
    setUbicacionQrBypass(true);
    setUbicacionQrBloqueo(false);
    setUbicacionQrFuera(false);
    setUbicacionQrComprobando(false);
  }, []);

  useEffect(() => {
    coordsQrRef.current = coordsQr;
  }, [coordsQr]);

  const verificarUbicacionQr = useCallback(async () => {
    if (!propuestaVotacion) return null;
    if (
      ubicacionQrBypass ||
      restauranteBypassRangoVotacionActivo(propuestaVotacion, data)
    ) {
      setUbicacionQrBloqueo(false);
      setUbicacionQrFuera(false);
      return coordsQrRef.current;
    }
    try {
      const c = await getCurrentUserPosition();
      setCoordsQr(c);
      setUbicacionQrBloqueo(false);
      const d = closestUbicacionKmForPropuesta(propuestaVotacion, c.latitude, c.longitude);
      setDistanciaQrClosest(d);
      setUbicacionQrFuera(distanciaVotoExcedeMaximoPermitido(d?.distanceKm));
      return c;
    } catch {
      setCoordsQr(null);
      setDistanciaQrClosest(null);
      setUbicacionQrFuera(false);
      setUbicacionQrBloqueo(true);
      return null;
    }
  }, [propuestaVotacion, ubicacionQrBypass, data]);

  const handleOmitirVerificacionUbicacionQr = useCallback(() => {
    if (!propuestaVotacion) return;
    const ref = referenciaCoordsPropuesta(propuestaVotacion);
    if (ref) {
      setCoordsQr({
        latitude: ref.lat,
        longitude: ref.lng,
        accuracy: null,
      });
      setDistanciaQrClosest(
        closestUbicacionKmForPropuesta(propuestaVotacion, ref.lat, ref.lng),
      );
    }
    setUbicacionQrBypass(true);
    setUbicacionQrFuera(false);
  }, [propuestaVotacion]);

  const bypassRangoRestauranteQrActivo = restauranteBypassRangoVotacionActivo(
    propuestaVotacion,
    data,
  );

  const ubicacionQrFueraBloqueante =
    ubicacionQrFuera && !ubicacionQrBypass && !bypassRangoRestauranteQrActivo;

  useEffect(() => {
    if (!bypassRangoRestauranteQrActivo) return;
    setUbicacionQrFuera(false);
    setUbicacionQrBloqueo(false);
    setUbicacionQrComprobando(false);
    if (propuestaVotacion) {
      aplicarCoordsBypassUbicacionQr(propuestaVotacion);
    }
  }, [
    bypassRangoRestauranteQrActivo,
    propuestaVotacion,
    aplicarCoordsBypassUbicacionQr,
  ]);

  const mensajeErrorUbicacionVotoQr = useMemo(() => {
    if (!ubicacionQrFueraBloqueante || !coordsQr) return null;
    return formatMensajeErrorUbicacionVotoUaUrMdd(
      coordsQr.latitude,
      coordsQr.longitude,
      distanciaQrClosest
    );
  }, [ubicacionQrFueraBloqueante, coordsQr, distanciaQrClosest]);

  useEffect(() => {
    if (!decoded) return;
    let cancelled = false;
    (async () => {
      const ok = await consumirUbicacionBypassVotarQr(decoded);
      if (cancelled || !ok) return;
      setUbicacionQrBypass(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [decoded]);

  useEffect(() => {
    if (!ubicacionQrBypass || !propuestaVotacion || !necesitaUbicacionParaVotarQr) return;
    aplicarCoordsBypassUbicacionQr(propuestaVotacion);
  }, [
    ubicacionQrBypass,
    propuestaVotacion,
    necesitaUbicacionParaVotarQr,
    aplicarCoordsBypassUbicacionQr,
  ]);

  useLayoutEffect(() => {
    if (!necesitaUbicacionParaVotarQr) {
      ubicacionQrVerificadaParaPropuestaRef.current = null;
      if (!ubicacionQrBypass) {
        setUbicacionQrBloqueo(false);
        setUbicacionQrFuera(false);
        setUbicacionQrComprobando(false);
        setCoordsQr(null);
        setDistanciaQrClosest(null);
      }
      return;
    }
    if (ubicacionQrBypass || bypassRangoRestauranteQrActivo) return;
    const propuestaId = participacion?.propuestaIdVotacion ?? '';
    if (ubicacionQrVerificadaParaPropuestaRef.current === propuestaId) return;
    ubicacionQrVerificadaParaPropuestaRef.current = propuestaId;
    let alive = true;
    setUbicacionQrComprobando(true);
    verificarUbicacionQr().finally(() => {
      if (alive) setUbicacionQrComprobando(false);
    });
    return () => {
      alive = false;
    };
  }, [
    necesitaUbicacionParaVotarQr,
    ubicacionQrBypass,
    bypassRangoRestauranteQrActivo,
    participacion?.propuestaIdVotacion,
    verificarUbicacionQr,
  ]);

  useEffect(() => {
    if (!necesitaUbicacionParaVotarQr || ubicacionQrBypass) return;
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      if (next !== 'active') return;
      setUbicacionQrComprobando(true);
      verificarUbicacionQr().finally(() => setUbicacionQrComprobando(false));
    });
    return () => sub.remove();
  }, [necesitaUbicacionParaVotarQr, ubicacionQrBypass, verificarUbicacionQr]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    if (!necesitaUbicacionParaVotarQr || ubicacionQrBypass) return;
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      setUbicacionQrComprobando(true);
      verificarUbicacionQr().finally(() => setUbicacionQrComprobando(false));
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [necesitaUbicacionParaVotarQr, ubicacionQrBypass, verificarUbicacionQr]);

  const handleRecargarTrasUbicacionQr = useCallback(() => {
    setUbicacionQrBypass(false);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }
    setUbicacionQrComprobando(true);
    verificarUbicacionQr().finally(() => setUbicacionQrComprobando(false));
  }, [verificarUbicacionQr]);

  const handleRecargarConBypassUbicacionQr = useCallback(() => {
    if (!decoded) return;
    marcarUbicacionBypassVotarQr(decoded);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.reload();
      return;
    }
    setUbicacionQrComprobando(true);
    setUbicacionQrBypass(true);
    if (propuestaVotacion) aplicarCoordsBypassUbicacionQr(propuestaVotacion);
  }, [decoded, propuestaVotacion, aplicarCoordsBypassUbicacionQr]);

  useEffect(() => {
    if (typeof document === 'undefined' || data === undefined) return;
    const name = data?.nombre_mostrar || data?.nombre_oficial || 'Restaurante';
    document.title = `${name} — As Gastronómico`;
  }, [data]);

  const panelEdicionRecuadroSlot = useMemo(() => {
    if (!participacion?.participa) return undefined;
    const raw = participacion.edicionLogotema?.trim();
    if (!raw) return undefined;
    const uri = getImageDisplayUrl(raw, { preset: 'logo' }) ?? raw.replace(/^http:/, 'https:');

    const nombreCiudad =
      propuestaVotacion?.restaurante?.ciudades?.nombre?.trim() ||
      (data != null && typeof data === 'object'
        ? (data as Restaurante).ciudad_nombre?.trim()
        : undefined) ||
      'Valencia';

    const textoBienvenidaLogotema = (
      <View style={styles.edActivaBienvenidaWrap}>
        <View style={styles.edActivaBienvenidaCabecera}>
          <Text style={styles.edActivaBienvenidaTitulo}>Bienvenido</Text>
          <Text style={styles.edActivaBienvenidaJuez}>¡Aquí tú eres el juez!</Text>
        </View>
        <Text style={styles.edActivaBienvenidaAyuda}>
          Ayúdanos a elegir el mejor <Text style={styles.edActivaBienvenidaAyudaPostre}>POSTRE</Text> de{' '}
          {nombreCiudad}.
        </Text>
        <Image
          source={{ uri: LOGO_AS_GASTRONOMICO_URI }}
          style={styles.edActivaLogoAsBienvenida}
          resizeMode="contain"
          accessibilityLabel="As Gastronómico"
        />
      </View>
    );

    if (participacion.propuestaIdVotacion) {
      return (
        <View style={styles.edActivaRecuadroSlot}>
          <Image source={{ uri }} style={styles.edActivaLogotema} resizeMode="contain" />
          {textoBienvenidaLogotema}
        </View>
      );
    }
    return (
      <View style={styles.edActivaRecuadroSlot}>
        <Image source={{ uri }} style={styles.edActivaLogotema} resizeMode="contain" />
        {textoBienvenidaLogotema}
        <Text style={styles.edActivaRecuadroPie}>
          Aún no hay una propuesta pública asociada para abrir la votación desde este enlace.
        </Text>
      </View>
    );
  }, [participacion, data, propuestaVotacion]);

  const slotMarcaPanelQr = useMemo(() => {
    if (!participacion?.participa || data == null || typeof data !== 'object') return undefined;
    const r = data as Restaurante;
    const nombreLocal = r.nombre_mostrar || r.nombre_oficial || 'Restaurante';
    const raw = r.logo || r.logos?.[r.logo_seleccionado_index ?? 0]?.url;
    const uri = raw ? getImageDisplayUrl(raw, { preset: 'card' }) ?? raw.replace(/^http:/, 'https:') : null;

    const logoEntreFotoYPanel = Boolean(participacion.propuestaIdVotacion && propuestaVotacion != null);

    if (logoEntreFotoYPanel) {
      return (
        <View
          style={[
            styles.slotMarcaQrConLogoPuente,
            propuestaVotacion.foto_restaurante
              ? styles.slotMarcaQrConLogoPuenteConFoto
              : styles.slotMarcaQrConLogoPuenteSinFotoHero,
          ]}
        >
          <View style={styles.logoPuenteMarco}>
            {uri ? (
              <Image source={{ uri }} style={styles.logoPuenteImagen} resizeMode="contain" />
            ) : (
              <View style={[styles.logoPuenteImagen, styles.logoPuenteImagenPh]}>
                <Ionicons name="restaurant" size={40} color="#684535" />
              </View>
            )}
          </View>
          <Text style={styles.panelTituloPropuestaQr} numberOfLines={3} translate="no">
            {propuestaVotacion.titulo?.trim() || 'Propuesta'}
          </Text>
          <Text style={styles.marcaNombreLocalQrSecundario}>{nombreLocal}</Text>
        </View>
      );
    }

    return (
      <View style={styles.marcaEnPanelMarrón}>
        {uri ? (
          <Image source={{ uri }} style={styles.marcaEnPanelLogo} resizeMode="contain" />
        ) : (
          <View style={[styles.marcaEnPanelLogo, styles.marcaEnPanelLogoPh]}>
            <Ionicons name="restaurant" size={36} color="rgba(255,255,255,0.75)" />
          </View>
        )}
        <Text style={styles.marcaEnPanelNombre}>{nombreLocal}</Text>
      </View>
    );
  }, [data, participacion, propuestaVotacion]);

  const qrNecesitaPropuestaVotacion = Boolean(
    participacion?.participa && participacion.propuestaIdVotacion,
  );
  const qrEsperandoUbicacionInicial =
    necesitaUbicacionParaVotarQr &&
    !bypassRangoRestauranteQrActivo &&
    !ubicacionQrBypass &&
    ubicacionQrComprobando;

  const qrPantallaCargando =
    data === undefined ||
    Boolean(data?.id && participacion === undefined) ||
    (qrNecesitaPropuestaVotacion && propuestaVotacion === undefined) ||
    qrEsperandoUbicacionInicial;

  if (qrPantallaCargando) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#A20000" />
        <Text style={styles.muted}>Cargando…</Text>
      </View>
    );
  }

  if (!data) {
    return (
      <View style={styles.centered}>
        <Ionicons name="restaurant-outline" size={48} color="#9ca3af" />
        <Text style={styles.errorTitle}>Restaurante no encontrado</Text>
        <Text style={styles.muted}>Este enlace no es válido o ya no está disponible.</Text>
        <TouchableOpacity onPress={() => navigate('/')} style={{ marginTop: 20 }} activeOpacity={0.75}>
          <Text style={styles.link}>Ir al inicio</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const nombre = data.nombre_mostrar || data.nombre_oficial || 'Restaurante';
  const logoRaw = data.logo || data.logos?.[data.logo_seleccionado_index ?? 0]?.url;
  const logoUri = logoRaw
    ? getImageDisplayUrl(logoRaw, { preset: 'card' }) ?? logoRaw.replace(/^http:/, 'https:')
    : null;

  /** Con votación activa, la identidad va en el panel marrón; no duplicar con la ficha blanca encima de la foto. */
  const mostrarFichaRestauranteSuperior = !participacion?.participa;

  const propuestaBypassUbicacionActivo = propuestaVotacion?.restaurante?.bypass_ubicacion === true;
  const propuestaBypassRangoRestauranteActivo = bypassRangoRestauranteQrActivo;

  const votarUbicacionBloquea =
    Boolean(participacion?.propuestaIdVotacion) &&
    !bypassRangoRestauranteQrActivo &&
    (!propuestaVotacion ||
      ubicacionQrComprobando ||
      ubicacionQrBloqueo ||
      ubicacionQrFueraBloqueante);

  const layoutQrPanelHastaFondo = Boolean(participacion?.participa);

  return (
    <View style={styles.qrRoot}>
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[
        styles.scrollInner,
        layoutQrPanelHastaFondo ? styles.scrollInnerQrPanelFill : null,
      ]}
    >
      {mostrarFichaRestauranteSuperior ? (
        <View style={styles.card}>
          {logoUri ? (
            <Image source={{ uri: logoUri }} style={styles.logo} resizeMode="contain" />
          ) : (
            <View style={[styles.logo, styles.logoPlaceholder]}>
              <Ionicons name="restaurant" size={40} color="#9ca3af" />
            </View>
          )}
          <Text style={styles.title}>{nombre}</Text>
          {data.breve_resena ? <Text style={styles.resena}>{data.breve_resena}</Text> : null}
        </View>
      ) : null}

      <View
        style={[
          styles.participacionWrap,
          !participacion?.participa ? styles.participacionWrapInset : null,
          layoutQrPanelHastaFondo ? styles.participacionWrapQrFill : null,
        ]}
      >
        {participacion?.participa ? (
          <View style={[styles.panelVotarStack, layoutQrPanelHastaFondo ? styles.panelVotarStackQrFill : null]}>
            {participacion.propuestaIdVotacion && propuestaVotacion ? (
              <PropuestaPortadaVotacion propuesta={propuestaVotacion} />
            ) : null}
            <PanelEstiloVotoRegistrado
              rellenarAlturaDisponible={layoutQrPanelHastaFondo}
              withSolapaFoto={Boolean(propuestaVotacion?.foto_restaurante)}
              slotEncimaRecuadro={slotMarcaPanelQr}
              noticeSlot={panelEdicionRecuadroSlot}
              noticeText={
                panelEdicionRecuadroSlot
                  ? undefined
                  : !participacion.propuestaIdVotacion
                    ? 'Aún no hay una propuesta pública asociada para abrir la votación desde este enlace.'
                    : undefined
              }
              primaryActionLabel={participacion.propuestaIdVotacion ? 'Votar' : undefined}
              primaryActionDisabled={votarUbicacionBloquea}
              onPrimaryAction={
                participacion.propuestaIdVotacion
                  ? () => navigate(`/votar/${participacion.propuestaIdVotacion}`)
                  : undefined
              }
            />
          </View>
        ) : participacion ? (
          <RestauranteEdicionActivaParticipacionCard loading={false} data={participacion} />
        ) : null}
      </View>
    </ScrollView>

      {necesitaUbicacionParaVotarQr &&
      ubicacionQrComprobando &&
      !qrPantallaCargando &&
      !ubicacionQrBloqueo &&
      !ubicacionQrFueraBloqueante ? (
        <View style={styles.ubicacionComprobandoOverlay} pointerEvents="auto">
          <ActivityIndicator size="large" color="#030213" />
          <Text style={styles.ubicacionComprobandoText}>Comprobando ubicación…</Text>
        </View>
      ) : null}

      <Modal
        visible={
          necesitaUbicacionParaVotarQr &&
          !bypassRangoRestauranteQrActivo &&
          (ubicacionQrBloqueo || ubicacionQrFueraBloqueante)
        }
        transparent
        animationType="fade"
        onRequestClose={() => {}}
      >
        <View style={styles.ubicacionModalBackdrop}>
          <View style={styles.ubicacionModalCard}>
            {ubicacionQrFueraBloqueante && !ubicacionQrBloqueo ? (
              <>
                {propuestaBypassRangoRestauranteActivo ? (
                  <TouchableOpacity
                    onPress={handleOmitirVerificacionUbicacionQr}
                    activeOpacity={1}
                    style={styles.ubicacionModalAlertIconPress}
                    hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
                    accessibilityRole="button"
                    accessibilityLabel="Continuar sin verificar ubicación en el local"
                  >
                    <Ionicons name="alert-circle-outline" size={40} color="#C50018" />
                  </TouchableOpacity>
                ) : (
                  <Ionicons
                    name="alert-circle-outline"
                    size={40}
                    color="#C50018"
                    style={{ alignSelf: 'center' }}
                  />
                )}
                <Text style={styles.ubicacionModalTitle}>¡Voto válido solo en el local!</Text>
                <Text style={styles.ubicacionModalBody}>
                  Para asegurar una votación justa y real, el sistema requiere verificar que estás en el local para
                  poder registrar tu voto.{'\n\n'}
                  ¡Gracias por ayudarnos a elegir el mejor postre!
                </Text>
                <TouchableOpacity
                  style={styles.ubicacionModalButton}
                  onPress={handleRecargarTrasUbicacionQr}
                  activeOpacity={0.88}
                >
                  <Ionicons name="refresh" size={20} color="#ffffff" />
                  <Text style={styles.ubicacionModalButtonText}>Recargar</Text>
                </TouchableOpacity>
                {mensajeErrorUbicacionVotoQr ? (
                  <Text style={styles.ubicacionModalErrorDebug} selectable>
                    {mensajeErrorUbicacionVotoQr}
                  </Text>
                ) : null}
              </>
            ) : (
              <>
                <Ionicons name="location-outline" size={40} color="#A20000" style={{ alignSelf: 'center' }} />
                <Text style={styles.ubicacionModalTitle}>Activa la ubicación</Text>
                <Text style={styles.ubicacionModalBody}>
                  Para continuar, necesitas el{' '}
                  <Text style={styles.ubicacionModalBodyBold}>servicio de ubicación</Text>
                  {' '}activado y permitir el acceso de{' '}
                  <Text style={styles.ubicacionModalBodyBold}>ubicación exacta</Text>
                  {' '}en el navegador y/o en los ajustes del dispositivo
                </Text>
                {!propuestaBypassUbicacionActivo ? (
                  <TouchableOpacity
                    style={styles.ubicacionModalButton}
                    onPress={handleRecargarTrasUbicacionQr}
                    activeOpacity={0.88}
                  >
                    <Ionicons name="refresh" size={20} color="#ffffff" />
                    <Text style={styles.ubicacionModalButtonText}>Recargar</Text>
                  </TouchableOpacity>
                ) : null}
                {propuestaBypassUbicacionActivo ? (
                  <TouchableOpacity
                    style={styles.ubicacionModalButton}
                    onPress={handleRecargarConBypassUbicacionQr}
                    activeOpacity={0.88}
                  >
                    <Ionicons name="refresh" size={20} color="#ffffff" />
                    <Text style={styles.ubicacionModalButtonText}>Recargar</Text>
                  </TouchableOpacity>
                ) : null}
              </>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  qrRoot: {
    flex: 1,
    position: 'relative',
  },
  scroll: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scrollInner: {
    paddingBottom: 40,
    maxWidth: 560,
    width: '100%',
    alignSelf: 'center',
  },
  scrollInnerQrPanelFill: {
    flexGrow: 1,
    paddingBottom: 0,
    ...(Platform.OS === 'web'
      ? ({ minHeight: '100vh' } as const)
      : ({ minHeight: '100%' } as const)),
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#f9fafb',
    gap: 12,
  },
  participacionWrap: {
    marginBottom: 8,
  },
  participacionWrapQrFill: {
    flex: 1,
    marginBottom: 0,
    alignSelf: 'stretch',
    minHeight: 0,
  },
  participacionWrapInset: {
    paddingHorizontal: 20,
  },
  panelVotarStack: {
    width: '100%',
    alignSelf: 'stretch',
  },
  panelVotarStackQrFill: {
    flex: 1,
    minHeight: 0,
  },
  edActivaRecuadroSlot: {
    width: '100%',
    alignItems: 'center',
    gap: 14,
  },
  edActivaBienvenidaWrap: {
    width: '100%',
    alignItems: 'center',
    marginTop: 2,
    paddingHorizontal: 4,
  },
  edActivaBienvenidaCabecera: {
    alignSelf: 'stretch',
    alignItems: 'center',
    gap: 2,
  },
  edActivaBienvenidaTitulo: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ffffff',
    textAlign: 'center',
    lineHeight: 28,
    marginBottom: 0,
  },
  edActivaBienvenidaJuez: {
    fontSize: 20,
    fontWeight: '700',
    color: '#ffffff',
    textAlign: 'center',
    lineHeight: 24,
    alignSelf: 'stretch',
    marginTop: 0,
  },
  edActivaBienvenidaAyuda: {
    fontSize: 16,
    fontWeight: '400',
    color: 'rgba(255, 255, 255, 0.92)',
    textAlign: 'center',
    lineHeight: 23,
    alignSelf: 'stretch',
    marginTop: 16,
  },
  edActivaBienvenidaAyudaPostre: {
    fontSize: 16,
    fontWeight: '700',
    color: 'rgba(255, 255, 255, 0.92)',
  },
  edActivaLogoAsBienvenida: {
    width: 138,
    height: 40,
    marginTop: 14,
    alignSelf: 'center',
  },
  edActivaLogotema: {
    width: '100%',
    maxWidth: 232,
    height: 82,
  },
  edActivaRecuadroPie: {
    fontSize: 15,
    fontWeight: '500',
    color: '#ffffff',
    textAlign: 'center',
    lineHeight: 22,
    alignSelf: 'stretch',
  },
  marcaEnPanelMarrón: {
    alignSelf: 'stretch',
    alignItems: 'center',
    gap: 12,
  },
  marcaEnPanelLogo: {
    width: 88,
    height: 88,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.12)',
  },
  marcaEnPanelLogoPh: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  marcaEnPanelNombre: {
    fontSize: 20,
    fontWeight: '700',
    color: '#ffffff',
    textAlign: 'center',
    alignSelf: 'stretch',
  },
  panelTituloPropuestaQr: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
    textAlign: 'center',
    alignSelf: 'stretch',
    lineHeight: 24,
    marginTop: 4,
    marginBottom: 6,
    opacity: 0.98,
  },
  /** Nombre del local bajo el título de la propuesta: más discreto que el titular. */
  marcaNombreLocalQrSecundario: {
    fontSize: 15,
    fontWeight: '500',
    color: 'rgba(255, 255, 255, 0.62)',
    textAlign: 'center',
    alignSelf: 'stretch',
    lineHeight: 20,
  },
  /** Logo a caballo entre portada y panel marrón (solo con propuesta cargada en QR). */
  slotMarcaQrConLogoPuente: {
    alignSelf: 'stretch',
    alignItems: 'center',
    marginBottom: 8,
    zIndex: 2,
  },
  slotMarcaQrConLogoPuenteConFoto: {
    marginTop: -82,
  },
  slotMarcaQrConLogoPuenteSinFotoHero: {
    marginTop: -64,
  },
  logoPuenteMarco: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: '#ffffff',
    borderWidth: 2,
    borderColor: 'rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: 4,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 8,
  },
  logoPuenteImagen: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 48,
  },
  logoPuenteImagenPh: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    margin: 20,
    padding: 24,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    alignItems: 'center',
    gap: 12,
  },
  logo: {
    width: 120,
    height: 120,
    borderRadius: 12,
    marginBottom: 8,
  },
  logoPlaceholder: {
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#030213',
    textAlign: 'center',
  },
  resena: {
    fontSize: 15,
    color: '#374151',
    lineHeight: 22,
    textAlign: 'center',
    marginTop: 4,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#030213',
    textAlign: 'center',
  },
  muted: {
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'center',
    maxWidth: 320,
  },
  link: {
    fontSize: 16,
    fontWeight: '600',
    color: '#A20000',
  },
  ubicacionComprobandoOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255,255,255,0.92)',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  ubicacionComprobandoText: {
    fontSize: 15,
    color: '#4b5563',
    fontWeight: '500',
  },
  ubicacionModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(3, 2, 19, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  ubicacionModalCard: {
    width: '100%',
    maxWidth: 400,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 24,
    gap: 14,
  },
  ubicacionModalAlertIconPress: {
    alignSelf: 'center',
    borderWidth: 0,
    ...(Platform.OS === 'web'
      ? ({ cursor: 'default', outlineStyle: 'none', border: 'none' } as const)
      : {}),
  },
  ubicacionModalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#030213',
    textAlign: 'center',
  },
  ubicacionModalBody: {
    fontSize: 15,
    color: '#4b5563',
    lineHeight: 22,
    textAlign: 'center',
  },
  ubicacionModalBodyBold: {
    fontWeight: '700',
    color: '#030213',
  },
  ubicacionModalCoords: {
    fontSize: 13,
    color: '#374151',
    lineHeight: 20,
    textAlign: 'center',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : Platform.OS === 'android' ? 'monospace' : 'monospace',
  },
  ubicacionModalErrorDebug: {
    marginTop: 12,
    fontSize: 12,
    fontWeight: '600',
    color: '#030213',
    lineHeight: 16,
    textAlign: 'center',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : Platform.OS === 'android' ? 'monospace' : 'monospace',
  },
  ubicacionModalButton: {
    marginTop: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#030213',
    paddingVertical: 14,
    borderRadius: 12,
  },
  ubicacionModalButtonDebajo: {
    marginTop: 10,
  },
  ubicacionModalButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
});
